import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Home, Lock, Eye, EyeOff, CheckCircle, AlertTriangle, 
  Loader2, User, Mail, ArrowRight 
} from 'lucide-react';
import { API } from '../utils/config';

export default function ActivateAccount() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    checkToken();
  }, [token]);

  const checkToken = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/activate-account/${token}/info`);
      
      if (response.data.valid) {
        setTokenInfo(response.data);
      } else {
        setError(response.data.error || 'Token non valido');
        if (response.data.already_activated) {
          setError('Questo account è già stato attivato. Vai al login.');
        }
      }
    } catch (err) {
      setError('Errore nel verificare il token. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = () => {
    if (password.length < 8) {
      setPasswordError('La password deve essere di almeno 8 caratteri');
      return false;
    }
    if (password !== confirmPassword) {
      setPasswordError('Le password non coincidono');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const handleActivate = async (e) => {
    e.preventDefault();
    
    if (!validatePassword()) return;
    
    setActivating(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API}/api/activate-account/${token}`, {
        password: password
      });
      
      if (response.data.success) {
        setSuccess(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante l\'attivazione. Riprova.');
    } finally {
      setActivating(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-blue-500 mb-4" />
            <p className="text-slate-600">Verifica in corso...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Account Attivato!</h2>
            <p className="text-slate-600 mb-6">
              Il tuo account è stato attivato con successo.<br />
              Ora puoi accedere alla piattaforma.
            </p>
            <Button 
              onClick={() => navigate('/login')}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Vai al Login
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state (invalid/expired token)
  if (error && !tokenInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-6">
              <AlertTriangle className="h-12 w-12 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Link Non Valido</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <div className="flex gap-3">
              <Button 
                variant="outline"
                onClick={() => navigate('/')}
              >
                Torna alla Home
              </Button>
              <Button 
                onClick={() => navigate('/login')}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Vai al Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Activation form
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Home className="h-8 w-8 text-blue-600" />
          </div>
          <CardTitle className="text-2xl">Attiva il tuo Account</CardTitle>
          <CardDescription>
            Benvenuto/a <strong>{tokenInfo?.first_name} {tokenInfo?.last_name}</strong>!<br />
            Scegli una password per accedere alla piattaforma.
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleActivate} className="space-y-4">
            {/* Email (read-only) */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email
              </Label>
              <Input 
                type="email"
                value={tokenInfo?.email || ''}
                disabled
                className="bg-slate-100"
              />
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Lock className="h-4 w-4" />
                Password
              </Label>
              <div className="relative">
                <Input 
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimo 8 caratteri"
                  className="pr-10"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Lock className="h-4 w-4" />
                Conferma Password
              </Label>
              <div className="relative">
                <Input 
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Ripeti la password"
                  className="pr-10"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Error messages */}
            {(passwordError || error) && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{passwordError || error}</AlertDescription>
              </Alert>
            )}

            {/* Submit button */}
            <Button 
              type="submit" 
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={activating || !password || !confirmPassword}
              data-testid="activate-btn"
            >
              {activating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Attivazione in corso...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Attiva Account
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-4 border-t text-center">
            <p className="text-sm text-slate-500">
              Hai già un account?{' '}
              <a href="/login" className="text-blue-600 hover:underline">
                Accedi qui
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
